<script lang="ts" setup>
import AuthAPI from '~/api/auth';

const TEST_ID = 'auth-email-form';

defineProps<{
  withTitle?: boolean;
}>();
const emit = defineEmits<{
  (event: 'forgot-password'): void;
  (event: 'to-phone'): void;
  (event: 'completed'): void;
}>();
const authorizationTheme = inject(
  INJECTION_KEYS.AuthorizationTheme,
  ThemeEnum.dark,
);

const { t } = useI18n();
const tokenStore = useTokenStore();

const isError = ref(false);
const email = ref('');
const password = ref('');

const { isValid, isLoading, execute, parameters } = useRequest(
  AuthAPI.loginAuth,
  () => ({ body: { login: email.value, password: password.value } }),
);

const onSubmit = () =>
  execute()
    ?.then((data) => {
      tokenStore.value = data.token;
      emit('completed');
    })
    .catch(() => {
      isError.value = true;
    });

watch(parameters, () => {
  isError.value = false;
});
</script>

<template>
  <form @submit.prevent="onSubmit" :data-test-id="TEST_ID">
    <h2 v-if="withTitle" class="authorizationComponent__title">
      {{ t('title') }}
    </h2>
    <div class="authorizationComponent__fields">
      <div>
        <BaseTextField
          v-model:value="email"
          type="email"
          autocomplete="email"
          inputmode="email"
          name="email"
          :placeholder="t('email_placeholder')"
          class="authorizationComponent__input"
          :bordered="authorizationTheme === ThemeEnum.light"
          :failed="isError"
          :data-test-id="`${TEST_ID}-email-input`"
        />
        <BaseCollapse :visible="isError" :fixed-duration="150">
          <span class="authorizationComponent__errorMessage">
            {{ t('error_message') }}
          </span>
        </BaseCollapse>
      </div>
      <BaseTextField
        v-model:value="password"
        type="password"
        autocomplete="current-password"
        :placeholder="t('password_placeholder')"
        class="authorizationComponent__input"
        :bordered="authorizationTheme === ThemeEnum.light"
        :data-test-id="`${TEST_ID}-password-input`"
      />
    </div>
    <div class="authorizationComponent__linkGroup">
      <button
        @click="emit('forgot-password')"
        class="authorizationComponent__link"
        type="button"
      >
        {{ t('forgot_password') }}
      </button>
      <button
        @click="emit('to-phone')"
        class="authorizationComponent__link"
        type="button"
      >
        {{ t('to_phone') }}
      </button>
    </div>

    <BaseButton
      :disabled="!isValid"
      :loading="isLoading"
      type="submit"
      class="authorizationComponent__submit"
      :dark="authorizationTheme === ThemeEnum.light"
      :data-test-id="`${TEST_ID}-submit`"
    >
      {{ t('submit') }}
    </BaseButton>
  </form>
</template>

<i18n>
ru:
  title: Войти или зарегистрироваться
  email_placeholder: E-mail
  password_placeholder: Пароль
  error_message: Неверный email или пароль
  forgot_password: Забыли пароль?
  to_phone: Войти по телефону
  submit: Войти

en:
  title: Log in or register
  email_placeholder: E-mail
  password_placeholder: Password
  error_message: Invalid email or password
  forgot_password: Forgot password?
  to_phone: Log in by phone
  submit: Log in
</i18n>
